import { reactive } from 'vue';
import { PUBLIC_RESOURCES_PATH, PUBLIC_RESOURCE_IMAGE_FILENAME_PATTERN } from '@/shared/constants/custom-styles';
import { PUBLIC_SETTINGS_PATH } from '@/shared/constants/api-paths';
import { normalizeApiBaseUrl, resolveApiOrigin } from '@/utils/api-url';
import { hasPublicResourcesSegment, prefixPublicResourcesIfMissing } from '@/utils/custom-style-resources';
import {
  EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY,
  configureExternalDomainAllowlist,
  isAllowlistedExternalUrl,
} from '@/utils/external-domain-allowlist';
import { normalizeAuthRedirectSetting } from '@/utils/settings-validation';
import { fetchPublicBootResource } from '@/utils/public-boot-fetch';
import { markBootConfigUnreachable } from '@/utils/boot-config-source';

export type PublicSettings = Record<string, unknown>;

export const publicSettings = reactive<PublicSettings>({});

function normalizePublicSettings(raw: Record<string, unknown> = {}): PublicSettings {
  // Public custom styles are applied pre-auth, long before normalizeAllApiSettings runs,
  // so the allowlist has to be established here or those URLs resolve against an empty one.
  configureExternalDomainAllowlist(raw[EXTERNAL_DOMAIN_ALLOWLIST_SETTING_KEY]);

  const logoutUrl = normalizeAuthRedirectSetting('logout_url', raw.logout_url ?? raw.logoutUrl);

  const normalized: PublicSettings = {
    ...raw,
    appName: raw.appName ?? raw.app_name,
    custom_css: raw.custom_css,
    custom_favicon: raw.custom_favicon ?? raw.customFavicon,
    custom_logo: raw.custom_logo ?? raw.customLogo,
    custom_login_background: raw.custom_login_background ?? raw.customLoginBackground,
    app_client_code: raw.app_client_code,
  };

  // loginUrl is deploy/nginx env (HELIX_LOGIN_URL) — never from the API (one source of truth).
  delete normalized.loginUrl;
  delete normalized.login_url;

  // error_url / internal_login_page are API redirect targets — SPA does not consume them.
  delete normalized.errorUrl;
  delete normalized.error_url;
  delete normalized.internalLoginPage;
  delete normalized.internal_login_page;
  delete normalized.internalLoginPath;

  if (logoutUrl !== undefined) {
    // SPA config uses camelCase; API key is logout_url (snake_case).
    normalized.logoutUrl = logoutUrl;
    normalized.logout_url = logoutUrl;
  } else {
    delete normalized.logoutUrl;
    delete normalized.logout_url;
  }

  return normalized;
}

export function publicResourceUrl(apiBaseUrl: unknown, file: unknown): string | null {
  const base = normalizeApiBaseUrl(apiBaseUrl);

  if (!base || typeof file !== 'string' || !PUBLIC_RESOURCE_IMAGE_FILENAME_PATTERN.test(file)) {
    return null;
  }

  return `${base}${PUBLIC_RESOURCES_PATH}${file}`;
}

function isAbsoluteHttpUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}

function isUrlOnConfiguredApiOrigin(value: string, apiBaseUrl: unknown): boolean {
  try {
    const url = new URL(value);
    const apiOrigin = resolveApiOrigin(apiBaseUrl);
    return Boolean(apiOrigin && url.origin === apiOrigin);
  } catch {
    return false;
  }
}

function logoSettingValue(settings: PublicSettings = {}): string | null {
  for (const key of ['login_logo_url', 'app_logo_url', 'custom_logo'] as const) {
    const value = settings[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

export function resolvePublicLogoUrl(apiBaseUrl: unknown, settings: PublicSettings = {}): string | null {
  const trimmed = logoSettingValue(settings);

  if (!trimmed) {
    return null;
  }

  if (isAbsoluteHttpUrl(trimmed)) {
    if (isAllowlistedExternalUrl(trimmed)) {
      return trimmed;
    }

    if (!isUrlOnConfiguredApiOrigin(trimmed, apiBaseUrl)) {
      return null;
    }

    try {
      const url = new URL(trimmed);
      return hasPublicResourcesSegment(url.pathname) ? trimmed : null;
    } catch {
      return null;
    }
  }

  return prefixPublicResourcesIfMissing(apiBaseUrl, trimmed);
}

export async function fetchPublicSettings(apiBaseUrl: unknown): Promise<PublicSettings> {
  const base = normalizeApiBaseUrl(apiBaseUrl);

  if (!base) {
    console.warn('[settings] Public settings skipped — HELIX_API_URL is unset; using bundled defaults.');
    return {};
  }

  const url = `${base}${PUBLIC_SETTINGS_PATH}`;
  const { response, outcome } = await fetchPublicBootResource(url, { label: 'GET /public/settings' });

  if (outcome === 'unreachable' || !response) {
    markBootConfigUnreachable('GET /public/settings unreachable');
    return {};
  }

  if (!response.ok) {
    console.warn(`[settings] GET /public/settings returned HTTP ${response.status}; continuing on bundled defaults.`);
    return {};
  }

  try {
    const data = await response.json();
    return normalizePublicSettings(data && typeof data === 'object' ? data : {});
  } catch (error) {
    console.warn('[settings] GET /public/settings returned invalid JSON; continuing on bundled defaults:', error);
    return {};
  }
}

export async function bootstrapPublicSettings(apiBaseUrl: unknown): Promise<PublicSettings> {
  // Always fetch anonymously on cold boot — do not skip for authenticated sessions.
  // Pre-auth API values (logo, app_client_code, custom_*, logoutUrl) must load every boot.
  const next = await fetchPublicSettings(apiBaseUrl);

  // Replace rather than merge — rejected auth paths must not leave a previous value in place.
  resetPublicSettingsState();
  Object.assign(publicSettings, next);
  return publicSettings;
}

/** Clears public bootstrap settings (logout / test reset). */
export function resetPublicSettingsState(): void {
  for (const key of Object.keys(publicSettings)) {
    delete publicSettings[key];
  }
}
